import * as Sentry from '@sentry/browser';
import { Severity } from '@sentry/types';

import { NO_ERROR_CODE } from './constants';
import { ErrorClientConfig, ErrorCustomTags } from './typings';

let clientConfig: ErrorClientConfig | null = null;

/**
 * Dispatches an exception to sentry.io
 * @param error The error object
 * @param contextData Any context data related to the exception
 * @param customTags
 */
export function dispatchException(
    error: Error,
    contextData: Record<string, any> | null = null,
    customTags: ErrorCustomTags | null = null,
): string {
    let eventId: string = NO_ERROR_CODE;

    if (clientConfig === null) {
        return eventId;
    }

    if (clientConfig.debug) {
        // eslint-disable-next-line no-console
        console.error('dispatchException', error, contextData);
    }

    Sentry.withScope((scope) => {
        if (contextData) {
            Object.keys(contextData).forEach((key) => {
                scope.setExtra(key, contextData[key]);
            });
        }
        if (customTags) {
            Object.keys(customTags).forEach((tagKey) => {
                scope.setTag(tagKey, customTags[tagKey]);
            });
        }
        eventId = Sentry.captureException(error);
    });

    return eventId;
}

/**
 * Dispatches a message to sentry.io
 * @param message The message to send
 * @param contextData Any context data related to the message
 * @param customTags
 * @param severity The severity to log the message under
 */
export function dispatchMessage(
    message: string,
    contextData: Record<string, any> | null,
    customTags: ErrorCustomTags | null,
    severity: Severity,
): string {
    let eventId: string = NO_ERROR_CODE;

    if (clientConfig === null) {
        return eventId;
    }

    if (clientConfig.debug) {
        // eslint-disable-next-line no-console
        console.error('dispatchMessage', message, contextData);
    }

    Sentry.withScope((scope) => {
        if (contextData) {
            Object.keys(contextData).forEach((key) => {
                scope.setExtra(key, contextData[key]);
            });
        }
        if (customTags) {
            Object.keys(customTags).forEach((tagKey) => {
                scope.setTag(tagKey, customTags[tagKey]);
            });
        }
        scope.setFingerprint([message]);
        eventId = Sentry.captureMessage(message, severity);
    });

    return eventId;
}

/**
 * Dispatches a critical severity message to sentry.io
 * @param message The message to send
 * @param contextData Any context data related to the message
 * @param customTags
 */
export function dispatchMessageCritical(
    message: string,
    contextData: Record<string, any> | null,
    customTags: ErrorCustomTags | null,
): string {
    return dispatchMessage(message, contextData, customTags, Severity.Critical);
}

/**
 * Dispatches a critical severity message to sentry.io
 * @param message The message to send
 * @param contextData Any context data related to the message
 * @param customTags
 */
export function dispatchMessageError(
    message: string,
    contextData: Record<string, any> | null,
    customTags: ErrorCustomTags | null,
): string {
    return dispatchMessage(message, contextData, customTags, Severity.Error);
}

/**
 * Initializes sentry.io
 * @param config The sentry.io config object
 */
export function initReporting(config: ErrorClientConfig): void {
    clientConfig = config;

    Sentry.init({
        integrations: (integrations) => {
            let excludedIntegrations = ['TryCatch', 'GlobalHandlers'];
            integrations = integrations.filter((integration) => !excludedIntegrations.includes(integration.name));
            return [
                ...integrations,
                new Sentry.Integrations.GlobalHandlers({
                    onunhandledrejection: false,
                    onerror: false,
                }),
            ];
        },
        beforeBreadcrumb(breadcrumb, hint) {
            let dataLength: number = 0;
            try {
                dataLength = JSON.stringify(breadcrumb.data).length;
            } catch (error) {
                dataLength = -1;
            }

            if (dataLength > 1024) {
                return {
                    ...breadcrumb,
                    data: {
                        message: `ERROR: data length (${dataLength}) was too long and has been replaced`,
                    },
                };
            }
            return breadcrumb;
        },
        ...clientConfig,
    });
}

/**
 * Reports a React error to sentry.io
 * @param error The error to report
 * @param componentInfo Information about the component in which the error occurred
 */
export function reportReactError(error: Error, componentInfo: any): string {
    return dispatchException(error, {
        componentInfo,
    });
}
